/**
 * Centralized Albanian UI copy — Phase 1 ships Albanian-only (V1, no
 * language switch). Every piece of journalist-visible interface chrome
 * (navigation, buttons, loading/empty/error states, section headings,
 * field labels, badges) is collected here rather than scattered as
 * inline string literals across dozens of components, so a future
 * wording change touches one file. This is deliberately NOT a full
 * i18n/multilingual platform — there is no runtime language selection —
 * but shaping it as one small module per surface means a future
 * additional language would mean adding a second file shaped like this
 * one and a lookup by locale, not rewriting every component.
 *
 * SCOPE — this file governs UI CHROME ONLY. It never translates:
 *   - stored Briefing prose (Sections 1/2/3/5's text, topic field VALUES
 *     like why_today/what_we_know, status/priority values) — these are
 *     already Albanian, generated by Claude per ai/prompt_manager.py's
 *     own prompt contract (see ai/parsers/daily_briefing_parser.py's
 *     `_TOPIC_FIELD_LABELS`, which recognizes Albanian labels like "Pse
 *     sot"/"Prioriteti" in Claude's raw output). Rendered unchanged.
 *   - the English section-key strings used as Briefing.content_json's
 *     `sections` dictionary KEYS (e.g. "1. Top Education Signals",
 *     hardcoded in ai/parsers/daily_briefing_parser.py's
 *     EXPECTED_SECTIONS). These are data identifiers, not display text
 *     — `sectionLabel()` below maps each one to its Albanian DISPLAY
 *     label without touching the lookup key itself.
 *   - open-ended, uncontrolled data values with no fixed vocabulary —
 *     EditorialCalendarEvent.category (free text, e.g. "school_year",
 *     "matura" — see backend/app/models/editorial_calendar_event.py's
 *     own comment listing examples) and Source.source_type (free text).
 *     Inventing a translation table for an open string field risks
 *     silently mistranslating a future value this file was never
 *     updated for; these render as stored (lightly formatted — see
 *     EventPanel.tsx). `verification_status` and `monitoring_frequency`
 *     ARE translated below because both are genuinely small, fixed
 *     vocabularies defined by backend/app/services (see their own
 *     comments) — an unrecognized value still falls back to the raw
 *     string rather than showing nothing.
 */

export const appName = {
  full: "Redaksia Inteligjente",
  short: "Redaksia",
};

export const nav = {
  today: "Sot",
  briefings: "Briefing-et",
  calendar: "Kalendari",
  sources: "Burimet",
  system: "Sistemi",
  mainNavigationLabel: "Navigimi kryesor",
  privateAccessOnly: "Vetëm qasje private.",
};

export const common = {
  retry: "Provo përsëri",
  loadingLabel: "Po ngarkohet",
  refreshing: "Po rifreskohet…",
  pageNotFoundTitle: "Faqja nuk u gjet",
  pageNotFoundDescription: "Kjo faqe nuk ekziston.",
  backToToday: "Kthehu te Sot",
};

const SECTION_LABELS: Record<string, string> = {
  "1. Top Education Signals": "1. Sinjalet Kryesore të Arsimit",
  "2. Documents To Review": "2. Dokumente për Shqyrtim",
  "3. Data Watch": "3. Vëzhgim i të Dhënave",
  "4. Investigation Opportunities": "4. Mundësi Hetimi",
  "5. Follow-Up Tracking": "5. Ndjekje e Vazhdimësisë",
};

/** Maps a stored section KEY (the exact dictionary key in content_json.sections — never translated, see this file's header) to its Albanian DISPLAY label. Falls back to the raw key for a section this map doesn't recognize, rather than showing nothing. */
export function sectionLabel(key: string): string {
  return SECTION_LABELS[key] ?? key;
}

export const today = {
  emptyTitle: "Ende nuk ka briefing të gjeneruar",
  emptyDescription:
    "Pipeline-i i mëngjesit funksionon pavarësisht nga kjo faqe — kontrollo përsëri pas ekzekutimit të radhës, ose hap Briefing-et për historikun.",
  errorTitle: "Briefingu i sotëm nuk u ngarkua dot",
};

export const briefingsList = {
  emptyTitle: "Ende nuk ka briefing-e",
  emptyDescription: "Briefing-et e gjeneruara do të shfaqen këtu pasi të ketë funksionuar pipeline-i i mëngjesit.",
  errorTitle: "Historiku i briefing-eve nuk u ngarkua dot",
  newer: "Më të reja",
  older: "Më të vjetra",
  rangeOfTotal: (from: number, to: number, total: number) => `${from}–${to} nga ${total}`,
  untitled: "Briefing pa titull",
  sectionsCount: (n: number) => `${n} seksione`,
  topicsCount: (n: number) => `${n} tema`,
  generatedAt: (formatted: string) => `Gjeneruar ${formatted}`,
};

export const briefingDetail = {
  allBriefings: "Të gjitha briefing-et",
  notFoundTitle: "Briefingu nuk u gjet",
  loadErrorTitle: "Ky briefing nuk u ngarkua dot",
};

export const briefingView = {
  delivered: "Dërguar",
  notDelivered: "Padërguar",
  missingSections: (n: number) => (n === 1 ? "1 seksion nuk u prodhua për këtë briefing." : `${n} seksione nuk u prodhuan për këtë briefing.`),
  notProduced: "Nuk u prodhua për këtë briefing.",
};

export const topic = {
  main: "KRYESORE",
  bonus: "BONUS",
  untitled: "Temë pa titull",
  confidenceScore: (n: number) => `${n}/100 siguri`,
  whyToday: "Pse ka rëndësi sot",
  centralQuestion: "Pyetja kryesore",
  whatWeKnow: "Çfarë dimë",
  whatWeDontKnow: "Çfarë nuk dimë ende",
  whatToVerify: "Çfarë duhet verifikuar",
  whoToContact: "Kë të kontaktosh",
  suggestedQuestions: "Pyetje të sugjeruara",
  possibleAngle: "Këndvështrimi i mundshëm",
  possibleOutcome: "Rezultati i mundshëm",
  basis: "Baza",
};

/** confidence label (stable, model-provided word — see lib/confidence.ts) -> Albanian display text. Semantics (which score range maps to which label) are untouched; only the displayed word changes. */
export const confidenceLabel: Record<"confirmed" | "strong_indication" | "possible_hypothesis" | "unknown", string> = {
  confirmed: "E konfirmuar",
  strong_indication: "Tregues i fortë",
  possible_hypothesis: "Hipotezë e mundshme",
  unknown: "E panjohur",
};

export const calendar = {
  errorTitle: "Kalendari nuk u ngarkua dot",
  legend: "Ditë me ngjarje sistemi",
  selectPrompt: "Zgjidh një datë për të parë ngjarjet e saj.",
  noEvents: "Nuk ka ngjarje kalendarike për këtë datë.",
  systemEvent: "Ngjarje sistemi",
  original: "Origjinali",
  sourcedOn: (formatted: string) => `Burimi: ${formatted}`,
  // react-day-picker's own ARIA label strings (see CalendarScreen.tsx's
  // `labels` override) default to English regardless of the `locale`
  // prop — locale only affects date-fns formatting of the date itself.
  labelPrevious: "Muaji i mëparshëm",
  labelNext: "Muaji tjetër",
  todayPrefix: "Sot, ",
  selectedSuffix: ", i zgjedhur",
};

/** verification_status is a small, fixed vocabulary (see backend/app/models/editorial_calendar_event.py) — unlike `category` (free text), safe to translate exhaustively. Falls back to the raw value (lightly formatted) for anything unrecognized. */
export const verificationStatus: Record<string, string> = {
  verified: "e verifikuar",
  partially_verified: "pjesërisht e verifikuar",
  unverified: "e paverifikuar",
};

export const sourcesList = {
  emptyTitle: "Asnjë burim i monitoruar",
  emptyDescription: "Nuk ka burime aktive, të monitoruara automatikisht, të konfiguruara.",
  errorTitle: "Gjendja e Burimeve nuk u ngarkua dot",
  tier: (n: number) => `Niveli ${n}`,
  lastSuccessfulCheck: (formatted: string) => `Kontrolli i fundit i suksesshëm: ${formatted}`,
  neverChecked: "Ende pa u kontrolluar me sukses",
  consecutiveFailures: (n: number) => (n === 1 ? "1 dështim i njëpasnjëshëm" : `${n} dështime të njëpasnjëshme`),
};

/** health is a fixed classification produced by classification.ts (a direct port of source_health.py) — always exactly one of these four. */
export const sourceHealthLabel: Record<"healthy" | "degraded" | "unhealthy" | "unknown", string> = {
  healthy: "Në rregull",
  degraded: "I dobësuar",
  unhealthy: "Jofunksional",
  unknown: "Pa kontroll",
};

/** monitoring_frequency is a small, fixed vocabulary (see backend/app/services/source_health.py's _STALENESS_WINDOWS). Falls back to the raw value for anything unrecognized (e.g. "manual" sources are already filtered out before reaching the UI, but this stays defensive rather than showing nothing). */
export const monitoringFrequencyLabel: Record<string, string> = {
  daily: "ditore",
  weekly: "javore",
  monthly: "mujore",
};

export const systemScreen = {
  coverageTitle: "Mbulimi i Grumbullimit",
  outputTitle: "Rezultatet",
  errorTitle: "Të dhënat e mbulimit nuk u ngarkuan dot",
  latestRun: (formatted: string) => `Ekzekutimi i fundit: ${formatted}`,
  noSourcesDue: "Asnjë burim nuk ishte i planifikuar për grumbullim në ekzekutimin e fundit.",
  successRate: (pct: number) => `Shkalla e suksesit: ${pct}%`,
  monitored: "Të monitoruara",
  attempted: "Të provuara",
  successful: "Të suksesshme",
  tier1Unhealthy: "Niveli-1 jofunksional",
  documentsCreated: "Dokumente të krijuara",
  signalsCreated: "Sinjale të krijuara",
  eligibleForEditorial: "Për shqyrtim editorial",
  backgroundOnly: "Vetëm si sfond",
  footerNote: "Diagnostikë infrastrukture — nuk shfaqet kurrë në vetë briefingun e gazetarit.",
};

/** coverage_status is a fixed classification produced by classification.ts (a direct port of coverage_metrics.py) — always exactly one of these four. */
export const coverageStatusLabel: Record<"good" | "degraded" | "poor" | "unknown", string> = {
  good: "Mirë",
  degraded: "E dobësuar",
  poor: "E dobët",
  unknown: "Pa ekzekutim të fundit",
};

export const distribution = {
  succeeded: (n: number) => `${n} të suksesshme`,
  failed: (n: number) => `${n} të dështuara`,
};
